import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map } from 'rxjs/operators';
import {
  Address,
  Country,
  Locality,
  Province,
  StreetType,
  STREET_TYPES_RIC
} from '../../models/address';
import { sortStrings } from '../../utils/utils';

@Injectable({
  providedIn: 'root'
})
export class AddressHelperService {
  constructor(private http: HttpClient) { }

  getProvincesList() {
    return this.http
      .get<Province[]>('assets/constants/provincesList.json')
      .pipe(
        map(provinces => {
          return this.orderProvincesByName(provinces);
        })
      );
  }

  getCountriesList() {
    return this.http
      .get<Country[]>('assets/constants/countriesList.json')
      .pipe(
        map(provinces => {
          return this.orderCountriesByName(provinces);
        })
      );
  }

  checkPostalCodeIsFromProvince(postalCode: string, province: Province) {
    if (
      !postalCode ||
      postalCode.length < 2 ||
      !province ||
      province.code == null
    ) {
      return false;
    }
    const provinceCode = this.getProvinceCodeFromPostalCode(postalCode);
    return provinceCode === province.code;
  }

  findProvinceByPostalCode(postalCode: string, provincesList: Province[]) {
    if (!postalCode) {
      return null;
    }
    const provinceCode = this.getProvinceCodeFromPostalCode(postalCode);
    return provincesList.find(province => province.code === provinceCode);
  }

  getProvinceCodeFromPostalCode(postalCode: string) {
    if (!postalCode || postalCode.length < 2) {
      return null;
    }
    if (postalCode.startsWith('0')) {
      return postalCode.substring(1, 2);
    }
    return postalCode.substring(0, 2);
  }

  getStreetTypes() {
    return this.http.get<StreetType[]>('assets/constants/streetTypes.json');
  }

  getDefaultStreetType(streetTypes: StreetType[]) {
    if (!streetTypes || !streetTypes.length) {
      return null;
    }
    return streetTypes.find(streetType => streetType.aciCode === 'CLL');
  }

  findStreetTypeByName(streetType: string, streetTypes: StreetType[]) {
    if (!streetTypes || !streetTypes.length) {
      return null;
    }
    let result = streetTypes.find(element => element.code === streetType);
    if (!result) {
      result = streetTypes.find(element => element.aciCode === streetType);
    }
    if (!result) {
      result = streetTypes.find(element => element.description === streetType);
    }
    if (!result) {
      result = streetTypes.find(element => element.code === 'CL');
    }
    return result;
  }

  findStreetTypeByAciCode(aciCode: string, streetTypes: StreetType[]) {
    if (!streetTypes || !streetTypes.length) {
      return null;
    }

    const result = streetTypes.find(s => s.aciCode === aciCode);
    return result;
  }

  findStreetTypeSinisterCodeByDescription(
    description: string,
    streetTypes: StreetType[]
  ) {
    const result = this.findStreetTypeByDescription(description, streetTypes);
    return result ? result.carSinisterCode : '';
  }

  findStreetTypeByDescription(
    description: string,
    streetTypes: StreetType[]
  ) {
    if (!streetTypes || !streetTypes.length) {
      return null;
    }

    return streetTypes.find(s => s.description === description);
  }

  orderCountriesByName(countriesList: Country[], descendant = false) {
    if (!countriesList || !countriesList.length) {
      return countriesList;
    }
    // Array.sort() method changes the input array
    return countriesList
      .map(country => country)
      .sort((a: Country, b: Country) => {
        return sortStrings(a.name, b.name, descendant);
      });
  }

  orderProvincesByName(provincesList: Province[], descendant = false) {
    if (!provincesList || !provincesList.length) {
      return provincesList;
    }
    // Array.sort() method changes the input array
    return provincesList
      .map(province => province)
      .sort((a: Province, b: Province) => {
        return sortStrings(a.name, b.name, descendant);
      });
  }

  orderLocalitiesByName(localitiesList: Locality[], descendant = false) {
    if (!localitiesList || !localitiesList.length) {
      return localitiesList;
    }
    // Array.sort() method changes the input array
    return localitiesList
      .map(locality => locality)
      .sort((a: Locality, b: Locality) => {
        return sortStrings(a.name, b.name, descendant);
      });
  }

  getAddressResume(address: Address, includeCountry = false) {
    if (address?.rawAddress && address?.rawAddress.trim()) {
      return address.rawAddress;
    }

    return this.composeAddressText(
      address,
      'address-resume',
      includeCountry,
      ' '
    );
  }

  getAddressResumeCustom(
    address: Address,
    includeCountry = false,
    separationText = ', '
  ) {
    return this.composeAddressText(
      address,
      'address-custom',
      includeCountry,
      separationText
    );
  }

  getRicStreetTypes() {
    return STREET_TYPES_RIC;
  }

  getMapAddressResume(address: Address) {
    if (!address) {
      return '';
    }
    const resumeComponents = [address.street, address.postalCode, address.city];
    const resume = resumeComponents
      .filter(component => !!component && !!component.trim())
      .join(' - ');
    return `${resume}${address.province ? `(${address.province})` : ''}`;
  }

  getPatrimonialPolicesAddressResume(address: Address) {
    return this.composeAddressText(address, 'patrimonial', false, ' ');
  }

  getPaymentPlanAddressResume(address: Address) {
    return this.composeAddressText(address, 'payment-plan', false, ' ');
  }

  private composeAddressText(
    address: Address,
    addressType:
      | 'address-resume'
      | 'address-custom'
      | 'payment-plan'
      | 'patrimonial',
    includeCountry: boolean,
    separationText: string
  ) {
    if (!address) {
      return '';
    }
    const addressComponents = [
      address.streetType,
      address.street,
      address.number,
      addressType === 'payment-plan' || addressType === 'address-resume'
        ? address.stair
        : null,
      address.floor,
      addressType !== 'address-custom' ? address.door : null,
      addressType !== 'patrimonial' ? address.postalCode : null,
      address.city ? address.city : address.locality,
      addressType !== 'patrimonial' ? address.province : null,
      includeCountry && address.countryLongName ? address.countryLongName : null
    ];
    return addressComponents
      .filter(component => !!component)
      .join(separationText);
  }
}
